/* eslint @typescript-eslint/explicit-function-return-type: 1, @typescript-eslint/no-explicit-any: 1 -- TODO fix types */
import { HttpClient } from '@angular/common/http';
import { Component, HostListener, inject, OnInit } from '@angular/core';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  Router,
  RouterOutlet,
} from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { setTheme } from 'ngx-bootstrap/utils';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  merge,
  Observable,
  of,
  retry,
  switchMap,
  tap,
  timer,
} from 'rxjs';

import {
  AutomationsStoreActions,
  InboxStoreActions,
  ListingStoreAction,
  ReservationStoreActions,
  UserStoreActions,
  UserStoreSelectors,
} from '@hosty-app/app-store';
import { NavigationService, sanitizeUrl, SentryService, UserService } from '@hosty-app/services';
import { ESocketEvent, ISocketMessage, Reservation, Task } from '@hosty-app/types';

import { convertMessageFromDto, MessageDto } from '@hosty-web/interfaces';
import { SupportChatService } from '@hosty-web/services';

import { AppStoreActions, AppStoreSelectors } from './core/store/app';
import { CentrifugeService } from './services/centrifugo.service';

/** Websocket reconnects run for the life of the session; only report the first few. */
const WS_FAILURES_REPORTED = 3;

// NavigationCancellationCode is a const enum, so it has no runtime reverse map.
const CANCELLATION_REASONS: Record<number, string> = {
  [NavigationCancellationCode.Redirect]: 'Redirect',
  [NavigationCancellationCode.SupersededByNewNavigation]: 'SupersededByNewNavigation',
  [NavigationCancellationCode.NoDataFromResolver]: 'NoDataFromResolver',
  [NavigationCancellationCode.GuardRejected]: 'GuardRejected',
};

@UntilDestroy()
@Component({
  selector: 'hosty-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [RouterOutlet],
})
export class AppComponent implements OnInit {
  public onNavigationEndEvent: Observable<any>;
  #centrifuge = inject(CentrifugeService);
  #http = inject(HttpClient);
  #supportChat = inject(SupportChatService);
  #nav = inject(NavigationService);
  #sentry = inject(SentryService);

  layout!: string;

  @HostListener('window:resize') updateLayout(): void {
    const width = document.body.clientWidth;
    this.store$.dispatch(AppStoreActions.updateClientWidth({ payload: { width } }));
  }

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly store$: Store,
  ) {
    setTheme('bs5');
    this.#supportChat.init();

    this.onNavigationEndEvent = this.router.events.pipe(filter((e) => e instanceof NavigationEnd));

    this.store$.select(AppStoreSelectors.selectActiveLayout).subscribe((l) => {
      document.body.classList.remove(this.layout);
      this.layout = `layout-${l?.name}`;
      document.body.classList.add(this.layout);
    });

    this.updateLayout();

    document.addEventListener('deviceready', () => {
      this.store$.dispatch(UserStoreActions.appLaunch());
    });
    document.addEventListener('resume', () => {
      this.store$.dispatch(UserStoreActions.appResume());
    });
  }

  ngOnInit(): void {
    this.#onRouteChange();
    this.#handleSignIn();
    this.#reportNavigationFailures();
    this.#identifyUser();
  }

  /**
   * A navigation that errors or is rejected leaves the user staring at the
   * previous screen with an apparently dead tab bar. Without this it is
   * invisible: the router swallows it and nothing reaches the console.
   */
  #reportNavigationFailures(): void {
    this.router.events
      .pipe(
        filter(
          (e): e is NavigationError | NavigationCancel =>
            e instanceof NavigationError ||
            (e instanceof NavigationCancel &&
              e.code !== NavigationCancellationCode.Redirect &&
              e.code !== NavigationCancellationCode.SupersededByNewNavigation),
        ),
        untilDestroyed(this),
      )
      .subscribe((event) => {
        if (event instanceof NavigationError) {
          this.#sentry.logEvent('Navigation failed', {
            url: sanitizeUrl(event.url),
            error: String(event.error),
          });
          return;
        }
        this.#sentry.logEvent('Navigation rejected', {
          url: sanitizeUrl(event.url),
          code: CANCELLATION_REASONS[event.code] ?? event.code,
          reason: event.reason,
        });
      });
  }

  #identifyUser(): void {
    this.store$
      .select(UserStoreSelectors.selectUserProfile)
      .pipe(untilDestroyed(this))
      .subscribe((profile) => {
        this.#sentry.setUser(profile ? String(profile.id) : null);
      });
  }

  #handleSignIn(): void {
    this.#initWS();
  }

  #onRouteChange(): void {
    merge([this.onNavigationEndEvent])
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        switchMap((route) => route.data),
        untilDestroyed(this),
      )
      .subscribe();
  }

  #initWS(): void {
    this.store$
      .select(UserStoreSelectors.selectUserProfile)
      .pipe(
        map((p) => p?.id ?? null),
        distinctUntilChanged(),
        filter((id) => !!id),
        switchMap((userId) =>
          timer(0, 120000).pipe(
            switchMap(() =>
              this.#http
                .get<{ last_updated_at: number }>('/api/v1/application/last-major-update')
                .pipe(catchError(() => of(null))),
            ),
            filter((r): r is { last_updated_at: number } => r !== null),
            map((r) => r.last_updated_at !== 1668729600),
            distinctUntilChanged(),
            tap((isNew) => this.#centrifuge.setVersionIsNew(isNew)),
            map((isNew) => ({ userId, legacy: !isNew })),
          ),
        ),
        switchMap(({ userId, legacy }) =>
          this.userService.getCentrifugeToken(legacy).pipe(
            retry(1),
            map((res) => ({ token: res.token, id: userId, legacy })),
            catchError((err) => {
              console.error('centrifuge token error', err);
              return EMPTY;
            }),
          ),
        ),
        switchMap(({ token, id, legacy }) => this.#connectWs(token, id, legacy)),
        // Last line of defence: an error escaping the socket stream used to tear
        // down this subscription for good, leaving the app running with no
        // real-time updates and no indication anything was wrong.
        retry({
          delay: (error, count) => {
            // Only the first few, and never on a schedule: this retries forever
            // by design, so reporting every attempt would emit an event every
            // 30s for the whole session and bury the signal it exists to give.
            if (count <= WS_FAILURES_REPORTED) {
              this.#sentry.logEvent('Websocket stream failed, reconnecting', {
                attempt: count,
                error: String(error),
              });
            }
            return timer(Math.min(count, 6) * 5000);
          },
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  #connectWs(token: string, userID: number, legacy: boolean): Observable<ISocketMessage<any>> {
    return this.#centrifuge.listen(token, userID, legacy).pipe(
      tap((data) => {
        this.store$.dispatch({
          type: `[Websocket] ${data.event}`,
          payload: data.data,
        });

        switch (data.event) {
          case ESocketEvent.NEW_MESSAGE: {
            const msg: MessageDto = data.data;
            if (msg.sent) {
              this.store$.dispatch(
                InboxStoreActions.newMessage({
                  message: convertMessageFromDto(msg),
                  fromSocket: true,
                }),
              );
            }

            break;
          }
          case ESocketEvent.ACCOUNT_STATUS_UPDATE: {
            this.store$.dispatch(ListingStoreAction.refresh());

            break;
          }
          case ESocketEvent.COUNTERS_UPDATED: {
            this.store$.dispatch(
              UserStoreActions.getCounterSuccess({
                payload: data.data,
              }),
            );
            break;
          }
          case ESocketEvent.RESERVATION_STATUS_UPDATE: {
            const reservation = new Reservation(data.data.reservation);
            this.store$.dispatch(
              ReservationStoreActions.setReservationUpdates({
                payload: {
                  reservation,
                },
              }),
            );
            this.store$.dispatch(
              InboxStoreActions.updateFeedReservationStatus({
                payload: {
                  reservation,
                  feedId: reservation?.feed?.id,
                },
              }),
            );
            break;
          }
          case ESocketEvent.I_CAL_UPDATE: {
            this.store$.dispatch(
              ListingStoreAction.updateICalStatus({
                payload: {
                  id: data.data.id,
                  status: data.data.status,
                  syncAt: data.data.sync_at,
                  listingId: data.data.listing.id,
                },
              }),
            );
            break;
          }

          case ESocketEvent.TASK_UPDATE: {
            this.store$.dispatch(
              AutomationsStoreActions.onTaskUpdated({
                payload: { task: new Task(data.data) },
              }),
            );
            break;
          }

          case ESocketEvent.TASK_CREATE: {
            this.store$.dispatch(
              AutomationsStoreActions.createTaskSuccess({
                payload: { task: new Task(data.data) },
              }),
            );
          }
        }
      }),
    );
  }
}
